export const ALLOWED_FILE_TYPE = "application/pdf" as const;
export const FILE_NAME_REGEX = /^[a-zA-Z0-9 _.-]+$/;
export const SAFE_NAME_REGEX = /^[A-Za-z0-9 ._-]+$/;
export const FILE_PATH_TRAVERSAL_REGEX = /\.\./;
export const FILE_DIRECTORY_SEPARATOR_REGEX = /[/\\]/;
export const FILE_INVALID_CHARS_REGEX = /[\\/:*?"<>|]/;

export const VALIDATION_LIMITS = {
  MAX_FILE_SIZE_MB: 5,
  MAX_NAME_LENGTH: 50,
  MAX_DESCRIPTION_LENGTH: 1000,
} as const;

export const UPLOAD_ERROR_MESSAGES = {
  NAME_REQUIRED: "Name is required",
  NAME_TOO_LONG: `Name must not exceed ${VALIDATION_LIMITS.MAX_NAME_LENGTH} characters`,
  NAME_INVALID_CHARS:
    "Name can only contain letters (A-Z, a-z), numbers, hyphens (-), dots (.), and underscores (_)",

  FILE_REQUIRED: "Please upload a PDF file",
  INVALID_FILE_TYPE: "Only PDF files are allowed",
  INVALID_FILE_NAME:
    "File name can only contain letters, numbers, spaces, hyphens (-), dots (.), and underscores (_)",
  FILE_TOO_LARGE: `File size must not exceed ${VALIDATION_LIMITS.MAX_FILE_SIZE_MB} MB`,

  DESCRIPTION_TOO_LONG: `Description must not exceed ${VALIDATION_LIMITS.MAX_DESCRIPTION_LENGTH} characters`,

  UPLOAD_FAILED_GENERIC: "An error occurred during upload. Please try again.",
  INITIATE_FAILED: "Failed to initiate upload",
  COMPLETE_FAILED: "Failed to complete upload",
  MISSING_PRESIGNED_URL: "Upload configuration is missing. Please try again.",
  MISSING_BLOB_PATH: "Upload destination could not be determined. Please try again.",
  FILE_PATH_TRAVERSAL: "File name must not contain path traversal sequences (..)",
  FILE_INVALID_CHARS: String.raw`File name cannot contain any of the following characters: \ / : * ? " < > |`,
  FILE_DIRECTORY_COMPONENT: String.raw`File name must not contain directory components (/ or \)`,
} as const;

export const PAGE_SIZE = 10;

export const STATUS_CLASSNAME = {
  completed: "statusCompleted",
  created: "statusCreated",
  failed: "statusFailed",
  inprogress: "statusInProgress",
} as const;

export const LIST_ERROR_MESSAGES = {
  LOAD_FAILED: "Failed to load documents",
  RETRY_PROMPT: "Failed to load documents. Please try again.",
} as const;

export type TabType = "document" | "pdf" | "compare";

export const PDF_CONFIG = {
  BASE_SCALE: 1.5,
  ZOOM_STEP: 0.25,
  MIN_ZOOM: 0.5,
  MAX_ZOOM: 3,
  DEFAULT_ZOOM: 1,
} as const;

/** Selectable zoom percentages for the PDF/converted-document zoom dropdowns, e.g. [50, 75, 100, ..., 300]. */
export const ZOOM_LEVEL_OPTIONS: readonly number[] = Array.from(
  { length: Math.round((PDF_CONFIG.MAX_ZOOM - PDF_CONFIG.MIN_ZOOM) / PDF_CONFIG.ZOOM_STEP) + 1 },
  (_, i) => Math.round((PDF_CONFIG.MIN_ZOOM + i * PDF_CONFIG.ZOOM_STEP) * 100),
);

export const DOCUMENT_STATUS = {
  COMPLETED: "completed",
  CREATED: "created",
  FAILED: "failed",
  IN_PROGRESS: "inprogress",
} as const;

export const DETAIL_ERROR_MESSAGES = {
  NO_ID: "No document ID provided",
  INVALID_ID: "Invalid document ID",
  FETCH_FAILED: "Failed to load document details",
  FETCH_ERROR: "Failed to load document details. Please try again.",
  PDF_LIBRARY_MISSING: "PDF.js library not loaded",
  PDF_LOAD_FAILED: "Failed to load PDF document. Please try again.",
  INVALID_PDF_URL: "Invalid PDF URL provided.",
  DOCX_LOAD_FAILED: "Failed to load the converted document. Please try again.",
  INVALID_DOCX_URL: "Invalid document URL provided.",
} as const;

export const LOADING_MESSAGES = {
  DOCUMENT: "Loading document details...",
  PDF: "Loading PDF document...",
  DOCX: "Loading converted document...",
  OPENING: "Opening document...",
} as const;

export function getStatusClassKey(status: string): keyof typeof STATUS_CLASSNAME {
  const normalised = status.toLowerCase();
  if (normalised === DOCUMENT_STATUS.COMPLETED) return "completed";
  if (normalised === DOCUMENT_STATUS.CREATED) return "created";
  if (normalised === DOCUMENT_STATUS.FAILED) return "failed";
  return "inprogress";
}
